import React, { useEffect, useState, useRef } from 'react';
import { Mail, Send, Inbox, ArrowUpRight, ChevronLeft, AlertTriangle, Lock, Settings, Paperclip, X, Download, FileText, Image, File } from 'lucide-react';
import useArrowNav from '../hooks/useArrowNav';
import PrintButton from '../components/PrintButton';

const MAX_ATTACHMENT_SIZE = 10 * 1024 * 1024; // 10 MB per file
const MAX_TOTAL_SIZE = 25 * 1024 * 1024; // 25 MB total

function formatFileSize(bytes) {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}

function getFileIcon(mimeType) {
  if (mimeType?.startsWith('image/')) return <Image size={14} />;
  if (mimeType?.includes('pdf') || mimeType?.includes('document') || mimeType?.includes('text')) return <FileText size={14} />;
  return <File size={14} />;
}

function EmailPage({ user }) {
  const [view, setView] = useState('inbox');      // inbox | sent | compose | read | smtp
  const [inbox, setInbox] = useState([]);
  const [sent, setSent] = useState([]);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const emailTabsRef = useRef(null);
  const { onKeyDown: handleEmailTabKeyDown } = useArrowNav(emailTabsRef, 'button', { vertical: false, activateOnFocus: true });

  // Compose state
  const [toUserIds, setToUserIds] = useState([]);
  const [ccUserIds, setCcUserIds] = useState([]);
  const [externalEmails, setExternalEmails] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [containsPhi, setContainsPhi] = useState(false);
  const [priority, setPriority] = useState('normal');
  const [isExternal, setIsExternal] = useState(false);
  const [sendStatus, setSendStatus] = useState('');

  // Attachment state
  const [attachments, setAttachments] = useState([]); // { file, filename, mimeType, size, data (base64), isPhi }
  const [attachmentError, setAttachmentError] = useState('');
  const fileInputRef = useRef(null);
  const isMountedRef = useRef(true);

  // SMTP config state
  const [smtpConfig, setSmtpConfig] = useState({ smtpHost: '', smtpPort: 587, smtpUser: '', smtpPass: '', fromName: '', fromEmail: '' });

  useEffect(() => {
    loadData();
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  async function loadData() {
    try {
      const [inboxData, sentData, users] = await Promise.all([
        window.zignagenixAPI.getEmailInbox(),
        window.zignagenixAPI.getEmailSent(),
        window.zignagenixAPI.getUsers(),
      ]);
      if (!inboxData?.error) setInbox(Array.isArray(inboxData) ? inboxData : []);
      if (!sentData?.error) setSent(Array.isArray(sentData) ? sentData : []);
      if (!users?.error) setAllUsers(Array.isArray(users) ? users.filter(u => u.is_active) : []);
    } catch (err) {
      console.error('Failed to load email data:', err);
    } finally {
      setLoading(false);
    }
  }

  async function openEmail(emailId) {
    try {
      const data = await window.zignagenixAPI.getEmail(emailId);
      if (!data?.error) {
        setSelectedEmail(data);
        setView('read');
      }
    } catch (err) {
      console.error('Failed to load email:', err);
    }
  }

  function startCompose() {
    setToUserIds([]); setCcUserIds([]); setExternalEmails('');
    setSubject(''); setBody(''); setContainsPhi(false);
    setPriority('normal'); setIsExternal(false); setSendStatus('');
    setAttachments([]); setAttachmentError('');
    setView('compose');
  }

  // ─── Attachment helpers ──────────────────────────────────────────────

  function handleFileSelect(e) {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setAttachmentError('');

    const currentTotal = attachments.reduce((sum, a) => sum + a.size, 0);

    for (const file of files) {
      if (file.size > MAX_ATTACHMENT_SIZE) {
        setAttachmentError(`"${file.name}" exceeds 10 MB limit`);
        continue;
      }
      if (currentTotal + file.size > MAX_TOTAL_SIZE) {
        setAttachmentError('Total attachments exceed 25 MB limit');
        break;
      }

      const reader = new FileReader();
      reader.onload = () => {
        if (!isMountedRef.current) return;
        const base64 = reader.result.split(',')[1]; // strip data:...;base64, prefix
        setAttachments(prev => {
          // Prevent duplicate filenames
          if (prev.find(a => a.filename === file.name)) return prev;
          return [...prev, {
            filename: file.name,
            mimeType: file.type || 'application/octet-stream',
            size: file.size,
            data: base64,
            isPhi: false,
          }];
        });
      };
      reader.readAsDataURL(file);
    }

    // Reset file input so the same file can be re-selected
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  function removeAttachment(filename) {
    setAttachments(prev => prev.filter(a => a.filename !== filename));
    setAttachmentError('');
  }

  function toggleAttachmentPhi(filename) {
    setAttachments(prev => prev.map(a => a.filename === filename ? { ...a, isPhi: !a.isPhi } : a));
  }

  // ─── Send ────────────────────────────────────────────────────────────

  async function handleSend() {
    setSendStatus('Sending...');
    try {
      let result;
      const attachmentPayload = attachments.map(a => ({
        filename: a.filename,
        mimeType: a.mimeType,
        size: a.size,
        data: a.data,
        isPhi: a.isPhi,
      }));

      if (isExternal) {
        const emails = externalEmails.split(',').map(e => e.trim()).filter(Boolean);
        if (emails.length === 0) { setSendStatus('Enter at least one external email'); return; }
        result = await window.zignagenixAPI.sendExternalEmail({
          subject, body, toEmails: emails, patientId: null, containsPhi, priority,
          attachments: attachmentPayload,
        });
      } else {
        if (toUserIds.length === 0) { setSendStatus('Select at least one recipient'); return; }
        result = await window.zignagenixAPI.sendInternalEmail({
          subject, body, recipientUserIds: toUserIds, ccUserIds, patientId: null, containsPhi, priority,
          attachments: attachmentPayload,
        });
      }
      if (result?.success) {
        setSendStatus('');
        loadData();
        setView('sent');
      } else {
        setSendStatus(result?.message || 'Send failed');
      }
    } catch (err) {
      setSendStatus('Send failed: ' + err.message);
    }
  }

  // ─── Download attachment ─────────────────────────────────────────────

  async function handleDownloadAttachment(att) {
    try {
      const result = await window.zignagenixAPI.downloadEmailAttachment(att.id);
      if (result?.error) {
        console.error('Download failed:', result.message);
        return;
      }
      // Convert base64 to blob and trigger browser download
      const byteChars = atob(result.data);
      const byteArray = new Uint8Array(byteChars.length);
      for (let i = 0; i < byteChars.length; i++) {
        byteArray[i] = byteChars.charCodeAt(i);
      }
      const blob = new Blob([byteArray], { type: result.mimeType });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = result.filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download attachment error:', err);
    }
  }

  // ─── SMTP Config ─────────────────────────────────────────────────────

  async function loadSmtpConfig() {
    try {
      const config = await window.zignagenixAPI.getSmtpConfig();
      if (config && !config.error) {
        setSmtpConfig({
          smtpHost: config.smtp_host || '', smtpPort: config.smtp_port || 587,
          smtpUser: config.smtp_user || '', smtpPass: '',
          fromName: config.from_name || '', fromEmail: config.from_email || '',
        });
      }
    } catch (err) { /* no config yet */ }
    setView('smtp');
  }

  async function saveSmtpConfig() {
    try {
      const result = await window.zignagenixAPI.saveSmtpConfig(smtpConfig);
      if (result?.success) setView('inbox');
    } catch (err) {
      console.error('Failed to save SMTP config:', err);
    }
  }

  if (loading) return <div className="page-loading">Loading secure email...</div>;

  return (
    <div className="page email-page">
      <div className="page-header-row">
        <div>
          <h1 className="page-title">Secure Email</h1>
          <p className="page-subtitle">HIPAA-compliant messaging — TLS encrypted for external, fully audited</p>
        </div>
        <PrintButton pageTitle="Secure Email" subtitle="HIPAA-Compliant Messaging" />
      </div>

      <div ref={emailTabsRef} className="email-toolbar" onKeyDown={handleEmailTabKeyDown} role="tablist">
        <button className={`email-tab ${view === 'inbox' ? 'email-tab--active' : ''}`} onClick={() => { setView('inbox'); setSelectedEmail(null); }} role="tab">
          <Inbox size={16} /> Inbox ({inbox.length})
        </button>
        <button className={`email-tab ${view === 'sent' ? 'email-tab--active' : ''}`} onClick={() => { setView('sent'); setSelectedEmail(null); }} role="tab">
          <ArrowUpRight size={16} /> Sent ({sent.length})
        </button>
        {user?.permissions?.includes('email:create') && (
          <button className="email-tab email-tab--compose" onClick={startCompose} role="tab">
            <Send size={16} /> Compose
          </button>
        )}
        {user?.permissions?.includes('email:config') && (
          <button className={`email-tab ${view === 'smtp' ? 'email-tab--active' : ''}`} onClick={loadSmtpConfig} role="tab">
            <Settings size={16} /> SMTP
          </button>
        )}
      </div>

      {/* ─── Inbox ─── */}
      {view === 'inbox' && (
        <div className="email-list">
          {inbox.map((e) => (
            <button key={e.id} className={`email-row ${!e.read_at ? 'email-row--unread' : ''}`} onClick={() => openEmail(e.id)}>
              <div className="email-row-from">{e.sender_name}</div>
              <div className="email-row-subject">
                {e.contains_phi && <span className="chat-phi-badge">PHI</span>}
                {e.priority === 'high' && <AlertTriangle size={12} className="icon--amber" />}
                {e.priority === 'urgent' && <AlertTriangle size={12} className="icon--red" />}
                {e.subject}
              </div>
              <div className="email-row-date">{e.created_at?.substring(0, 16)}</div>
            </button>
          ))}
          {inbox.length === 0 && <p className="empty-state">No messages in inbox</p>}
        </div>
      )}

      {/* ─── Sent ─── */}
      {view === 'sent' && (
        <div className="email-list">
          {sent.map((e) => (
            <button key={e.id} className="email-row" onClick={() => openEmail(e.id)}>
              <div className="email-row-from">To: {e.recipient_names || 'Unknown'}</div>
              <div className="email-row-subject">
                {e.contains_phi && <span className="chat-phi-badge">PHI</span>}
                {e.message_type === 'outbound' && <Lock size={12} className="icon--green" />}
                {e.subject}
              </div>
              <div className="email-row-date">{e.created_at?.substring(0, 16)}</div>
            </button>
          ))}
          {sent.length === 0 && <p className="empty-state">No sent messages</p>}
        </div>
      )}

      {/* ─── Read Email ─── */}
      {view === 'read' && selectedEmail && (
        <div className="email-read">
          <button className="btn-sm btn-ghost" onClick={() => setView('inbox')}><ChevronLeft size={16} /> Back</button>
          <div className="email-read-header">
            <h2>{selectedEmail.subject}</h2>
            <div className="email-read-meta">
              From: <strong>{selectedEmail.sender_name || 'System'}</strong> |
              Date: {selectedEmail.created_at} |
              {selectedEmail.message_type === 'outbound' && <span><Lock size={12} /> TLS Encrypted</span>}
              {selectedEmail.contains_phi && <span className="chat-phi-badge">PHI</span>}
            </div>
            {selectedEmail.recipients && (
              <div className="email-read-recipients">
                To: {selectedEmail.recipients.filter(r => r.recipient_type === 'to').map(r => r.user_name || r.external_email).join(', ')}
                {selectedEmail.recipients.filter(r => r.recipient_type === 'cc').length > 0 && (
                  <span> | CC: {selectedEmail.recipients.filter(r => r.recipient_type === 'cc').map(r => r.user_name || r.external_email).join(', ')}</span>
                )}
              </div>
            )}
          </div>
          <div className="email-read-body">{selectedEmail.body}</div>

          {/* ─── Attachments on read ─── */}
          {selectedEmail.attachments && selectedEmail.attachments.length > 0 && (
            <div className="email-attachments-section">
              <h3 className="email-attachments-title"><Paperclip size={14} /> Attachments ({selectedEmail.attachments.length})</h3>
              <div className="email-attachments-list">
                {selectedEmail.attachments.map((att) => (
                  <div key={att.id} className="email-attachment-item">
                    <span className="email-attachment-icon">{getFileIcon(att.mime_type)}</span>
                    <span className="email-attachment-name">{att.filename}</span>
                    <span className="email-attachment-size">{formatFileSize(att.file_size)}</span>
                    {att.is_phi && <span className="chat-phi-badge" style={{ fontSize: 10 }}>PHI</span>}
                    <button className="btn-sm btn-ghost" onClick={() => handleDownloadAttachment(att)} title="Download">
                      <Download size={14} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ─── Compose ─── */}
      {view === 'compose' && (
        <div className="email-compose">
          <div className="email-compose-toggle">
            <button className={`btn-sm ${!isExternal ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setIsExternal(false)}>Internal</button>
            {user?.permissions?.includes('email:send_external') && (
              <button className={`btn-sm ${isExternal ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setIsExternal(true)}>
                <Lock size={12} /> External (SMTP)
              </button>
            )}
          </div>

          {!isExternal ? (
            <div className="email-field">
              <label className="email-label">To:</label>
              <select multiple className="email-multi-select" value={toUserIds} onChange={(e) => setToUserIds(Array.from(e.target.selectedOptions, o => Number(o.value)))}>
                {allUsers.map(u => <option key={u.id} value={u.id}>{u.display_name} ({u.role})</option>)}
              </select>
              <label className="email-label">CC:</label>
              <select multiple className="email-multi-select email-multi-select--sm" value={ccUserIds} onChange={(e) => setCcUserIds(Array.from(e.target.selectedOptions, o => Number(o.value)))}>
                {allUsers.map(u => <option key={u.id} value={u.id}>{u.display_name}</option>)}
              </select>
            </div>
          ) : (
            <div className="email-field">
              <label className="email-label">To (external emails, comma-separated):</label>
              <input className="email-input" placeholder="user@example.com, other@example.com" value={externalEmails} onChange={(e) => setExternalEmails(e.target.value)} />
              <div className="email-tls-notice"><Lock size={12} /> External emails are sent via TLS-encrypted SMTP (HIPAA §164.312(e)(1))</div>
            </div>
          )}

          <div className="email-field">
            <label className="email-label">Subject:</label>
            <input className="email-input" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Message subject" />
          </div>

          <div className="email-field">
            <label className="email-label">Message:</label>
            <textarea className="email-textarea" rows={10} value={body} onChange={(e) => setBody(e.target.value)} placeholder="Type your message..." />
          </div>

          {/* ─── Attachment picker ─── */}
          <div className="email-field">
            <label className="email-label"><Paperclip size={14} /> Attachments:</label>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              style={{ display: 'none' }}
              onChange={handleFileSelect}
            />
            <button className="btn-sm btn-ghost" onClick={() => fileInputRef.current?.click()}>
              <Paperclip size={14} /> Add Files
            </button>
            <span className="email-attachment-hint">Max 10 MB per file, 25 MB total</span>
            {attachmentError && <div className="email-attachment-error">{attachmentError}</div>}

            {attachments.length > 0 && (
              <div className="email-attachments-list" style={{ marginTop: 8 }}>
                {attachments.map((att) => (
                  <div key={att.filename} className="email-attachment-item">
                    <span className="email-attachment-icon">{getFileIcon(att.mimeType)}</span>
                    <span className="email-attachment-name">{att.filename}</span>
                    <span className="email-attachment-size">{formatFileSize(att.size)}</span>
                    <label className="email-attachment-phi-toggle" title="Mark as PHI">
                      <input type="checkbox" checked={att.isPhi} onChange={() => toggleAttachmentPhi(att.filename)} />
                      <span style={{ fontSize: 11 }}>PHI</span>
                    </label>
                    <button className="btn-sm btn-ghost email-attachment-remove" onClick={() => removeAttachment(att.filename)} title="Remove">
                      <X size={14} />
                    </button>
                  </div>
                ))}
                <div className="email-attachment-total">
                  Total: {formatFileSize(attachments.reduce((s, a) => s + a.size, 0))} / {formatFileSize(MAX_TOTAL_SIZE)}
                </div>
              </div>
            )}
          </div>

          <div className="email-compose-footer">
            <label className="chat-compose-toggle">
              <input type="checkbox" checked={containsPhi} onChange={(e) => setContainsPhi(e.target.checked)} />
              <span>Contains PHI</span>
            </label>
            <select className="chat-priority-select" value={priority} onChange={(e) => setPriority(e.target.value)}>
              <option value="normal">Normal</option>
              <option value="high">High Priority</option>
              <option value="urgent">Urgent</option>
            </select>
            <button className="btn-primary" onClick={handleSend} disabled={!subject.trim() || !body.trim()}>
              <Send size={16} /> Send {isExternal ? '(Encrypted)' : ''} {attachments.length > 0 ? `+ ${attachments.length} file${attachments.length > 1 ? 's' : ''}` : ''}
            </button>
            <button className="btn-ghost" onClick={() => setView('inbox')}>Cancel</button>
            {sendStatus && <span className="email-send-status">{sendStatus}</span>}
          </div>
        </div>
      )}

      {/* ─── SMTP Config ─── */}
      {view === 'smtp' && (
        <div className="email-smtp-config">
          <h2 className="section-title"><Settings size={18} /> SMTP Configuration</h2>
          <p className="page-subtitle">Configure outbound email server (TLS required for HIPAA compliance)</p>
          <div className="settings-grid" style={{ marginTop: 16 }}>
            <div className="settings-item">
              <label className="email-label">SMTP Host</label>
              <input className="email-input" value={smtpConfig.smtpHost} onChange={(e) => setSmtpConfig({...smtpConfig, smtpHost: e.target.value})} placeholder="smtp.gmail.com" />
            </div>
            <div className="settings-item">
              <label className="email-label">SMTP Port</label>
              <input className="email-input" type="number" value={smtpConfig.smtpPort} onChange={(e) => setSmtpConfig({...smtpConfig, smtpPort: Number(e.target.value)})} />
            </div>
            <div className="settings-item">
              <label className="email-label">SMTP Username</label>
              <input className="email-input" value={smtpConfig.smtpUser} onChange={(e) => setSmtpConfig({...smtpConfig, smtpUser: e.target.value})} />
            </div>
            <div className="settings-item">
              <label className="email-label">SMTP Password</label>
              <input className="email-input" type="password" value={smtpConfig.smtpPass} onChange={(e) => setSmtpConfig({...smtpConfig, smtpPass: e.target.value})} placeholder="Enter new password" />
            </div>
            <div className="settings-item">
              <label className="email-label">From Name</label>
              <input className="email-input" value={smtpConfig.fromName} onChange={(e) => setSmtpConfig({...smtpConfig, fromName: e.target.value})} placeholder="ZignaGenix" />
            </div>
            <div className="settings-item">
              <label className="email-label">From Email</label>
              <input className="email-input" value={smtpConfig.fromEmail} onChange={(e) => setSmtpConfig({...smtpConfig, fromEmail: e.target.value})} placeholder="noreply@zignagenix.com" />
            </div>
          </div>
          <div style={{ marginTop: 16, display: 'flex', gap: 8 }}>
            <button className="btn-primary" onClick={saveSmtpConfig}>Save Configuration</button>
            <button className="btn-ghost" onClick={() => setView('inbox')}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default EmailPage;
